import { computed, shallowRef } from 'vue';
import { defineStore } from 'pinia';
import { $fetch } from '@/utils/base-fetch';
import {
  DEFAULT_LANGUAGE,
  HEADER_KEY_LANGUAGE,
  HEADER_KEY_SITE,
  SITE_CODE,
} from '@/utils/constants';
import type { PublicSiteResponse } from './types';

const useAppStore = defineStore('app', () => {
  const sidebarOpen = shallowRef(false);

  // 全站公开站点配置：服务端拉取后通过 __INITIAL_STATE__ 透传给客户端，避免重复请求
  const site = shallowRef<PublicSiteResponse | null>(null);
  const siteLoaded = shallowRef(false);

  // 派生：供 header/footer/SEO 直接消费
  const siteName = computed(() => site.value?.name ?? '');
  const siteTitle = computed(() => site.value?.title ?? '');
  const siteDescription = computed(() => site.value?.description ?? '');
  const siteLogoUrl = computed(() => site.value?.logoUrl ?? '');
  const siteSeo = computed(() => site.value?.seo ?? null);

  // 客户端 hydrate：若服务端已注入站点配置，直接复用，跳过首次请求
  if (typeof window !== 'undefined' && window.__INITIAL_STATE__?.app?.site) {
    const state = window.__INITIAL_STATE__.app as { site?: PublicSiteResponse };

    if (state.site) {
      site.value = state.site;
      siteLoaded.value = true;
    }
  }

  const toggleSidebar = () => {
    sidebarOpen.value = !sidebarOpen.value;
  };

  const closeSidebar = () => {
    sidebarOpen.value = false;
  };

  async function fetchSite() {
    // 公开配置失败静默降级，不 toast 打扰访客
    const { isSuccess, data } = await $fetch<PublicSiteResponse>(
      '/api/public/v1/sites/get',
      {
        errorMessage: false,
        headers: {
          [HEADER_KEY_SITE]: SITE_CODE,
          [HEADER_KEY_LANGUAGE]: DEFAULT_LANGUAGE,
        },
      },
    );

    if (isSuccess && data) {
      site.value = data;
      siteLoaded.value = true;
    }
  }

  async function ensureSiteLoaded() {
    if (siteLoaded.value) {
      return;
    }

    await fetchSite();
  }

  async function refreshSite() {
    siteLoaded.value = false;

    await ensureSiteLoaded();
  }

  return {
    sidebarOpen,
    site,
    siteLoaded,
    siteName,
    siteTitle,
    siteDescription,
    siteLogoUrl,
    siteSeo,
    toggleSidebar,
    closeSidebar,
    ensureSiteLoaded,
    refreshSite,
  };
});

export default useAppStore;
